import { Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { gridStringOrNumberComparator } from '@mui/x-data-grid-pro';
import Decimal from 'decimal.js';
import { useMemo, useState } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { Card } from '@/components/layout/Card/Card';
import { TableHeaderBar } from '@/components/tables/components/TableHeaderBar/TableHeaderBar';
import { CellContainer } from '@/components/tables/DataTable/components/cells';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { TextRenderer } from '@/components/tables/DataTable/renderers/cell/TextRenderer';
import { TableEmptyState } from '@/components/tables/DataTable/TableEmptyState';
import {
  getIntegratedAssetsFromValuation,
  getManuallyTrackedAssetsFromValuation,
} from '@/modules/assets/FullScreenStructuredAssetsModal/FullScreenStructuredAssetsModal.utils';
import { AssetData } from '@/modules/assets/FullScreenStructuredAssetsModal/IntegrationValuationSummary';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { StructuredAssetsSubform_ValuationFragment } from '@/modules/entities/StructuredAssetsSubform/graphql/StructuredAssetsSubform.generated';
import { EstatePage_EntityFragment } from '@/modules/estate/graphql/EstatePageData.generated';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { getIntegrationProviderName } from '@/modules/household/LinkHouseholdDataTrowser/DirectHoldingsStep/hooks/directHoldingsStepData.utils';
import { AssetValuationV2ValuationSource } from '@/types/schema';
import {
  formatCurrencyNoDecimals,
  formatCurrencyNoDecimalsAccounting,
} from '@/utils/formatting/currency';

import { EditAssetSideDrawer } from './EditAssetSideDrawer';

const MAX_ASSET_COUNT = 10;
export interface HoldingsGroupAssetsProps {
  holding: EstatePage_EntityFragment;
  valuation: StructuredAssetsSubform_ValuationFragment | null;
  integratedAssets: ReturnType<typeof getIntegratedAssetsFromValuation>;
  manuallyTrackedAssets: ReturnType<
    typeof getManuallyTrackedAssetsFromValuation
  >;
}

export function HoldingsGroupAssets({
  holding,
  valuation,
  integratedAssets,
  manuallyTrackedAssets,
}: HoldingsGroupAssetsProps) {
  const totalAssetsValue = useMemo(
    () =>
      [...integratedAssets, ...manuallyTrackedAssets].reduce(
        (sum, asset) =>
          sum.plus(asset.assetValue?.ownedValue ?? new Decimal(0)),
        new Decimal(0)
      ),
    [integratedAssets, manuallyTrackedAssets]
  );

  const hasIntegratedAssets = integratedAssets.length > 0;
  const hasManuallyTrackedAssets = manuallyTrackedAssets.length > 0;

  if (!hasManuallyTrackedAssets && !hasIntegratedAssets) {
    return null;
  }

  return (
    <Card variant="outlined">
      <Stack
        key={holding.id}
        sx={{
          p: 2,
          borderRadius: '4px',
        }}
      >
        <Stack gap={2}>
          <Stack>
            <Typography variant="title-s">Assets</Typography>
            <Typography variant="body-xs">
              {formatCurrencyNoDecimals(totalAssetsValue)} total
            </Typography>
          </Stack>
          {hasManuallyTrackedAssets && (
            <HoldingsGroupAssetsInner
              holding={holding}
              valuation={valuation}
              assets={manuallyTrackedAssets}
              variant={'manually-tracked'}
            />
          )}
          {hasIntegratedAssets && (
            <HoldingsGroupAssetsInner
              holding={holding}
              valuation={valuation}
              assets={integratedAssets}
              variant={'integrated'}
            />
          )}
        </Stack>
      </Stack>
    </Card>
  );
}

type HoldingsGroupAssetsInnerPropsVariant = 'manually-tracked' | 'integrated';

export interface HoldingsGroupAssetsInnerProps {
  holding: EstatePage_EntityFragment;
  valuation: StructuredAssetsSubform_ValuationFragment | null;
  assets:
    | ReturnType<typeof getIntegratedAssetsFromValuation>
    | ReturnType<typeof getManuallyTrackedAssetsFromValuation>;
  variant: HoldingsGroupAssetsInnerPropsVariant;
}

export function HoldingsGroupAssetsInner({
  holding,
  valuation,
  assets,
  variant,
}: HoldingsGroupAssetsInnerProps) {
  const canEdit = useCanEditHouseholdData();
  const { householdIntegrationLink } = useHouseholdDetailsContext();
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);

  const handleEditClick = () => {
    setEditDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setEditDrawerOpen(false);
  };

  const rows = useMemo(
    () =>
      assets.map((asset) => ({
        id: asset.id,
        assetCategory: asset.class?.displayName ?? 'Unknown',
        title: asset.displayName,
        value: asset.assetValue?.ownedValue ?? new Decimal(0),
      })),
    [assets]
  );
  const hasValidIntegrationValuation =
    variant == 'integrated' &&
    valuation &&
    valuation.valuationSource !== AssetValuationV2ValuationSource.Manual;

  // Create columns with edit action for manually tracked assets
  const columns = useMemo(() => {
    // Reorder columns and rename Title to Name
    const reorderedColumns = [
      { field: 'title', headerName: 'Name', flex: 2 }, // Name first
      { field: 'assetCategory', headerName: 'Asset category', flex: 1 }, // Category second
      {
        // Value column
        field: 'value',
        headerName: 'Value',
        flex: 1,
        renderCell: TextRenderer({
          text: ({ row }) => formatCurrencyNoDecimalsAccounting(row.value),
        }),
        sortComparator: gridStringOrNumberComparator,
      },
    ];
    const baseColumns = reorderedColumns;

    // Only add edit button for manually tracked assets, but always add the column so that
    // the tables line up in terms of columns
    const actionsColumn: GridColDef<AssetData> = {
      field: 'actions',
      headerName: '',
      width: 50,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell:
        variant === 'manually-tracked'
          ? ({ row: _row }: { row: AssetData }) => (
              <CellContainer align="center">
                <IconButton
                  ariaLabel="Edit asset"
                  size="xs"
                  variant="transparent"
                  icon={Edit02Icon}
                  onClick={() => handleEditClick()}
                  data-testid="edit-asset-button"
                />
              </CellContainer>
            )
          : undefined,
    };
    if (canEdit) {
      return [...baseColumns, actionsColumn];
    } else {
      return [...baseColumns];
    }
  }, [variant, canEdit]);

  return (
    <>
      <Stack>
        <TableHeaderBar
          header={
            variant == 'manually-tracked'
              ? 'Manually tracked assets'
              : `Assets linked to ${getIntegrationProviderName(householdIntegrationLink)}`
          }
        />
        <DataTable
          columns={columns}
          rows={rows}
          slots={{
            noRowsOverlay: () => (
              <TableEmptyState
                text={
                  hasValidIntegrationValuation
                    ? 'No assets found in this valuation.'
                    : 'Detailed assets will appear after saving and replace any existing data.'
                }
              />
            ),
          }}
          pageSize={MAX_ASSET_COUNT}
          pagination={true}
          hideFooterPagination={rows.length <= MAX_ASSET_COUNT}
          getRowHeight={() => 60}
          paginationMode="client"
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          disableRowSelectionOnClick
          noPointer
          rowHoverVariant="transparent"
        />
      </Stack>
      <EditAssetSideDrawer
        isOpen={editDrawerOpen}
        onClose={handleDrawerClose}
        entity={holding}
        existingAssets={variant === 'manually-tracked' ? assets : []}
        valuation={valuation}
      />
    </>
  );
}
